import React, { useState } from "react";
import Select from "react-select";
import { selectStyles } from "../../../../utils/selectStyles";
import {
  createPhysicalEntityCounterparty,
  createLegalEntityCounterparty,
  findCounterpartyByBinOrInn,
  getBinOrInnDuplicateMessage,
  isDuplicateBinOrInnApiError,
} from "../../../../api/api";
import "./AddCounterpartyModal.css";

const AddCounterpartyModal = ({
  open,
  onClose,
  onCounterpartyCreated,
  defaultType = "counterparty",
}) => {
  const [counterpartyType, setCounterpartyType] = useState(defaultType);
  const isQuickRecipient = defaultType === "counterparty";
  const isOrganization = isQuickRecipient
    ? false
    : counterpartyType === "organization";

  const [legalForm, setLegalForm] = useState(null);
  const [binIin, setBinIin] = useState("");
  const [fullName, setFullName] = useState("");
  const [shortName, setShortName] = useState("");
  const [personFullName, setPersonFullName] = useState("");
  const [organizationPhone, setOrganizationPhone] = useState("");
  const [organizationEmail, setOrganizationEmail] = useState("");
  const [personPhone, setPersonPhone] = useState("");
  const [personEmail, setPersonEmail] = useState("");
  const [isNdsPayer, setIsNdsPayer] = useState(true);

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const legalFormOptions = [
    { value: "ТОО", label: "ТОО" },
    { value: "ИП", label: "ИП" },
    { value: "Физическое лицо на ГПХ", label: "Физическое лицо на ГПХ" },
  ];

  const isIndividualPerson =
    legalForm?.value === "ИП" ||
    legalForm?.value?.includes("Физическое лицо");

  const resetForm = () => {
    setCounterpartyType(defaultType);
    setLegalForm(null);
    setBinIin("");
    setFullName("");
    setShortName("");
    setPersonFullName("");
    setOrganizationPhone("");
    setOrganizationEmail("");
    setPersonPhone("");
    setPersonEmail("");
    setIsNdsPayer(true);
    setErrors({});
    setSubmitError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateForm = () => {
    const newErrors = {};

    if (!legalForm)
      newErrors.legalForm = "Выберите организационно-правовую форму";

    if (!binIin) newErrors.binIin = "Введите БИН/ИИН";
    else if (binIin.length !== 12)
      newErrors.binIin = "Введите корректный БИН/ИИН (12 цифр)";

    if (isIndividualPerson) {
      if (!personFullName.trim())
        newErrors.personFullName = isQuickRecipient
          ? "Введите полное наименование"
          : "Введите ФИО";
    } else {
      if (!fullName.trim()) newErrors.fullName = "Введите полное наименование";
      if (!shortName.trim())
        newErrors.shortName = "Введите сокращенное наименование";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const normalizeEmptyStringsToNull = (obj) => {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        typeof value === "string" && value.trim() === "" ? null : value,
      ])
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const existingByBin = await findCounterpartyByBinOrInn(binIin);
      if (existingByBin) {
        setErrors((prev) => ({
          ...prev,
          binIin: getBinOrInnDuplicateMessage(existingByBin),
        }));
        setIsSubmitting(false);
        return;
      }

      const isLegalEntityForm = legalForm?.value === "ТОО";
      const isPhysicalEntityForm =
        legalForm?.value === "ИП" ||
        legalForm?.value === "Физическое лицо на ГПХ";

      const commonFields = isQuickRecipient
        ? {
            is_nds_payer: isNdsPayer,
            side_type: "counterparty",
            legal_form: legalForm?.value || null,
            bin_or_inn: binIin,
            is_actual_equals_legal: true,
          }
        : normalizeEmptyStringsToNull({
            is_nds_payer: isNdsPayer,
            side_type: isOrganization ? "our_company" : "counterparty",
            legal_form: legalForm?.value || null,
            bin_or_inn: binIin || null,
            country: null,
            city: null,
            street: null,
            building: null,
            postal_code: null,
            is_actual_equals_legal: true,
            phone: (isIndividualPerson ? personPhone : organizationPhone) || null,
            email: (isIndividualPerson ? personEmail : organizationEmail) || null,
          });

      let result;

      if (isLegalEntityForm) {
        if (isQuickRecipient) {
          const legalBody = {
            ...commonFields,
            type: "legal_entity",
            full_company_name: fullName || null,
            short_company_name: shortName || null,
          };
          result = await createLegalEntityCounterparty(legalBody);
        } else {
          const legalBody = {
            ...commonFields,
            type: "legal_entity",
            full_company_name: fullName || null,
            short_company_name: shortName || null,
            full_name_first_supervisor: null,
            job_title: null,
          };
          result = await createLegalEntityCounterparty(
            normalizeEmptyStringsToNull(legalBody)
          );
        }
      } else if (isPhysicalEntityForm) {
        if (isQuickRecipient) {
          const physicalBody = {
            ...commonFields,
            type: "physical_entity",
            full_name: personFullName || null,
          };
          result = await createPhysicalEntityCounterparty(physicalBody);
        } else {
          const physicalBody = {
            ...commonFields,
            type: "physical_entity",
            full_name: personFullName || null,
            document_type: null,
            document_number: null,
            document_date_of_issue: null,
            document_validity_period: null,
            document_issued_by: null,
          };
          result = await createPhysicalEntityCounterparty(
            normalizeEmptyStringsToNull(physicalBody)
          );
        }
      } else {
        throw new Error(
          "Выберите организационно-правовую форму: ТОО, ИП или Физическое лицо на ГПХ"
        );
      }

      if (onCounterpartyCreated) {
        onCounterpartyCreated(result);
      }

      handleClose();
    } catch (error) {
      console.error("Ошибка при создании контрагента:", error);
      if (isDuplicateBinOrInnApiError(error.message)) {
        setErrors((prev) => ({
          ...prev,
          binIin: "Контрагент с таким БИН/ИИН уже существует",
        }));
      }
      setSubmitError(
        error.message || "Не удалось создать контрагента. Попробуйте еще раз."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="add-counterparty-modal-backdrop" onClick={handleClose} />
      <div className="add-counterparty-modal">
        <div className="add-counterparty-modal-header">
          <h2 className="add-counterparty-modal-title">
            {isOrganization ? "Добавить организацию" : "Добавить контрагента"}
          </h2>
          <button
            type="button"
            className="add-counterparty-modal-close"
            onClick={handleClose}
          >
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="add-counterparty-modal-content">
            {!isQuickRecipient && (
              <div className="add-counterparty-modal-section">
                <label className="add-counterparty-modal-label">
                  Тип контрагента
                </label>
                <div className="add-counterparty-modal-radio-group">
                  <label className="add-counterparty-modal-radio-label">
                    <input
                      type="radio"
                      name="counterpartyType"
                      value="counterparty"
                      checked={counterpartyType === "counterparty"}
                      onChange={(e) => setCounterpartyType(e.target.value)}
                    />
                    <span className="add-counterparty-modal-radio-custom"></span>
                    Контрагент
                  </label>
                  <label className="add-counterparty-modal-radio-label">
                    <input
                      type="radio"
                      name="counterpartyType"
                      value="organization"
                      checked={counterpartyType === "organization"}
                      onChange={(e) => setCounterpartyType(e.target.value)}
                    />
                    <span className="add-counterparty-modal-radio-custom"></span>
                    Наша компания
                  </label>
                </div>
              </div>
            )}

            <div className="add-counterparty-modal-section">
              <label className="add-counterparty-modal-label">
                Организационно-правовая форма{" "}
                <span className="add-counterparty-modal-required">*</span>
              </label>
              <Select
                value={legalForm}
                onChange={setLegalForm}
                options={legalFormOptions}
                placeholder="Выберите ОПФ"
                styles={selectStyles}
                classNamePrefix="select"
              />
              {errors.legalForm && (
                <span className="add-counterparty-modal-error">
                  {errors.legalForm}
                </span>
              )}
            </div>

            <div className="add-counterparty-modal-section">
              <label className="add-counterparty-modal-label">
                БИН/ИИН{" "}
                <span className="add-counterparty-modal-required">*</span>
              </label>
              <div className="add-counterparty-modal-input-wrapper">
                <input
                  type="text"
                  value={binIin}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, "");
                    if (value.length <= 12) {
                      setBinIin(value);
                      if (errors.binIin) {
                        setErrors((prev) => ({ ...prev, binIin: undefined }));
                      }
                    }
                  }}
                  placeholder="Введите 12 цифр"
                  maxLength={12}
                  className={`add-counterparty-modal-input ${
                    errors.binIin ? "error" : ""
                  }`}
                />
                {binIin.length === 12 && (
                  <span className="add-counterparty-modal-check">✓</span>
                )}
              </div>
              {errors.binIin && (
                <span className="add-counterparty-modal-error">
                  {errors.binIin}
                </span>
              )}
            </div>

            {isQuickRecipient ? (
              <>
                <div className="add-counterparty-modal-section">
                  <label className="add-counterparty-modal-label">
                    Полное наименование{" "}
                    <span className="add-counterparty-modal-required">*</span>
                  </label>
                  <div className="add-counterparty-modal-input-wrapper">
                    <input
                      type="text"
                      value={isIndividualPerson ? personFullName : fullName}
                      onChange={(e) =>
                        isIndividualPerson
                          ? setPersonFullName(e.target.value)
                          : setFullName(e.target.value)
                      }
                      placeholder={
                        isIndividualPerson
                          ? "Введите ФИО"
                          : 'Например, ТОО "СтройПроект Астана"'
                      }
                      className={`add-counterparty-modal-input ${
                        (isIndividualPerson
                          ? errors.personFullName
                          : errors.fullName)
                          ? "error"
                          : ""
                      }`}
                    />
                    {(isIndividualPerson ? personFullName : fullName) && (
                      <span className="add-counterparty-modal-check">✓</span>
                    )}
                  </div>
                  {isIndividualPerson ? (
                    errors.personFullName && (
                      <span className="add-counterparty-modal-error">
                        {errors.personFullName}
                      </span>
                    )
                  ) : (
                    errors.fullName && (
                      <span className="add-counterparty-modal-error">
                        {errors.fullName}
                      </span>
                    )
                  )}
                </div>

                <div className="add-counterparty-modal-section">
                  <label className="add-counterparty-modal-label">
                    Сокращенное наименование{" "}
                    {!isIndividualPerson && (
                      <span className="add-counterparty-modal-required">*</span>
                    )}
                  </label>
                  <div className="add-counterparty-modal-input-wrapper">
                    <input
                      type="text"
                      value={shortName}
                      onChange={(e) => setShortName(e.target.value)}
                      placeholder={
                        isIndividualPerson
                          ? "Можно не заполнять"
                          : "Введите сокращённое наименование"
                      }
                      className={`add-counterparty-modal-input ${
                        !isIndividualPerson && errors.shortName ? "error" : ""
                      }`}
                      disabled={isIndividualPerson}
                    />
                    {!isIndividualPerson && shortName && (
                      <span className="add-counterparty-modal-check">✓</span>
                    )}
                  </div>
                  {!isIndividualPerson && errors.shortName && (
                    <span className="add-counterparty-modal-error">
                      {errors.shortName}
                    </span>
                  )}
                </div>
              </>
            ) : isIndividualPerson ? (
              <div className="add-counterparty-modal-section">
                <label className="add-counterparty-modal-label">
                  ФИО <span className="add-counterparty-modal-required">*</span>
                </label>
                <div className="add-counterparty-modal-input-wrapper">
                  <input
                    type="text"
                    value={personFullName}
                    onChange={(e) => setPersonFullName(e.target.value)}
                    placeholder="Введите ФИО"
                    className={`add-counterparty-modal-input ${
                      errors.personFullName ? "error" : ""
                    }`}
                  />
                  {personFullName && (
                    <span className="add-counterparty-modal-check">✓</span>
                  )}
                </div>
                {errors.personFullName && (
                  <span className="add-counterparty-modal-error">
                    {errors.personFullName}
                  </span>
                )}
              </div>
            ) : (
              <>
                <div className="add-counterparty-modal-section">
                  <label className="add-counterparty-modal-label">
                    Полное наименование{" "}
                    <span className="add-counterparty-modal-required">*</span>
                  </label>
                  <div className="add-counterparty-modal-input-wrapper">
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder='Например, ТОО "СтройПроект Астана"'
                      className={`add-counterparty-modal-input ${
                        errors.fullName ? "error" : ""
                      }`}
                    />
                    {fullName && (
                      <span className="add-counterparty-modal-check">✓</span>
                    )}
                  </div>
                  {errors.fullName && (
                    <span className="add-counterparty-modal-error">
                      {errors.fullName}
                    </span>
                  )}
                </div>

                <div className="add-counterparty-modal-section">
                  <label className="add-counterparty-modal-label">
                    Сокращенное наименование{" "}
                    <span className="add-counterparty-modal-required">*</span>
                  </label>
                  <div className="add-counterparty-modal-input-wrapper">
                    <input
                      type="text"
                      value={shortName}
                      onChange={(e) => setShortName(e.target.value)}
                      placeholder="Введите сокращённое наименование"
                      className={`add-counterparty-modal-input ${
                        errors.shortName ? "error" : ""
                      }`}
                    />
                    {shortName && (
                      <span className="add-counterparty-modal-check">✓</span>
                    )}
                  </div>
                  {errors.shortName && (
                    <span className="add-counterparty-modal-error">
                      {errors.shortName}
                    </span>
                  )}
                </div>
              </>
            )}

            {!isQuickRecipient && (
              <div className="add-counterparty-modal-row">
                <div className="add-counterparty-modal-section add-counterparty-modal-half">
                  <label className="add-counterparty-modal-label">Телефон</label>
                  <input
                    type="tel"
                    value={isIndividualPerson ? personPhone : organizationPhone}
                    onChange={(e) =>
                      isIndividualPerson
                        ? setPersonPhone(e.target.value)
                        : setOrganizationPhone(e.target.value)
                    }
                    placeholder="+7..."
                    className="add-counterparty-modal-input"
                  />
                </div>

                <div className="add-counterparty-modal-section add-counterparty-modal-half">
                  <label className="add-counterparty-modal-label">E-mail</label>
                  <input
                    type="email"
                    value={isIndividualPerson ? personEmail : organizationEmail}
                    onChange={(e) =>
                      isIndividualPerson
                        ? setPersonEmail(e.target.value)
                        : setOrganizationEmail(e.target.value)
                    }
                    placeholder="Введите E-mail"
                    className="add-counterparty-modal-input"
                  />
                </div>
              </div>
            )}

            <div className="add-counterparty-modal-section">
              <label className="add-counterparty-modal-label">
                Плательщик НДС <span className="add-counterparty-modal-required">*</span>
              </label>
              <div className="add-counterparty-modal-radio-group">
                <label className="add-counterparty-modal-radio-label">
                  <input
                    type="radio"
                    name="isNdsPayer"
                    value="true"
                    checked={isNdsPayer === true}
                    onChange={() => setIsNdsPayer(true)}
                  />
                  <span className="add-counterparty-modal-radio-custom"></span>
                  Да
                </label>
                <label className="add-counterparty-modal-radio-label">
                  <input
                    type="radio"
                    name="isNdsPayer"
                    value="false"
                    checked={isNdsPayer === false}
                    onChange={() => setIsNdsPayer(false)}
                  />
                  <span className="add-counterparty-modal-radio-custom"></span>
                  Нет
                </label>
              </div>
            </div>

            {submitError && (
              <div className="add-counterparty-modal-submit-error">
                {submitError}
              </div>
            )}
          </div>

          <div className="add-counterparty-modal-actions">
            <button
              type="button"
              onClick={handleClose}
              className="add-counterparty-modal-btn-cancel"
              disabled={isSubmitting}
            >
              Отмена
            </button>
            <button
              type="submit"
              className="add-counterparty-modal-btn-submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Создание..." : "Создать"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default AddCounterpartyModal;
